import React, { useState, useEffect, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import './CustomDateTimePicker.css';
import "react-datepicker/dist/react-datepicker.css";

const CustomDateTimePicker = forwardRef(({ 
  // Support both prop naming conventions
  selectedDate, 
  onDateChange, 
  value,
  onChange,
  showTimeSelect = true,
  timeFormat = "HH:mm",
  timeIntervals = 15,
  dateFormat = "MMMM d, yyyy HH:mm",
  minDate,
  placeholder = "Select date and time",
  ...otherProps 
}, ref) => {
  
  // Use value/onChange if provided, otherwise fallback to selectedDate/onDateChange
  const currentValue = value !== undefined ? value : selectedDate;
  const handleChange = onChange || onDateChange;
  
  const [tempDate, setTempDate] = useState(currentValue);
  const [isOpen, setIsOpen] = useState(false);

  // Sync tempDate when external value changes
  useEffect(() => {
    setTempDate(currentValue);
  }, [currentValue]);

  // Set minimum date to current time in Athens timezone
  const getMinDate = () => {
    if (minDate) return minDate;
    
    // Create current date in Athens timezone
    const now = new Date();
    const athensTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Athens"}));
    return athensTime;
  };

  const handleOK = () => {
    if (tempDate && handleChange) {
      // Ensure the date is treated as Athens timezone
      const athensDate = new Date(tempDate.toLocaleString("en-US", {timeZone: "Europe/Athens"}));
      handleChange(tempDate); // Keep original date object to preserve user's selection
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempDate(currentValue);
    setIsOpen(false);
  };

  const handleOpenPicker = () => {
    setTempDate(currentValue);
    setIsOpen(true);
  };

  const handleDateChange = (date) => {
    setTempDate(date);
  };

  const formatSelectedDate = () => {
    if (!currentValue) return placeholder;
    
    try {
      // Format in Athens timezone
      const athensDateStr = currentValue.toLocaleString("en-GB", {
        timeZone: "Europe/Athens",
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      return athensDateStr.replace(',', '');
    } catch (error) {
      console.error('Date formatting error:', error);
      return placeholder;
    }
  };

  if (!isOpen) {
    return (
      <div className="datepicker-wrapper">
        <button 
          onClick={handleOpenPicker}
          className="picker-input-btn w-full p-4 bg-white border border-black/10 rounded-xl focus:ring-1 focus:ring-[#ba1609] focus:border-[#ba1609] ring-offset-0 transition-colors duration-300 outline-none focus:outline-none text-left text-[#1a1012]"
          type="button"
        >
          {formatSelectedDate()}
        </button>
      </div>
    );
  }
  
  return (
    <div className="datepicker-wrapper">
      <div className="datepicker-container">
        <DatePicker
          ref={ref}
          selected={tempDate}
          onChange={handleDateChange}
          showTimeSelect={showTimeSelect}
          timeFormat={timeFormat}
          timeIntervals={timeIntervals}
          dateFormat={dateFormat}
          timeCaption="Time"
          inline
          minDate={getMinDate()}
          popperClassName="custom-datepicker-popper"
          {...otherProps}
        />
        <div className="picker-confirm-container">
          <button 
            type="button" 
            className="picker-cancel-btn bg-white hover:bg-[#fbeceb] text-[#ba1609] border border-[#ba1609] px-4 py-2 rounded mr-2 transition-colors"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="picker-confirm-btn bg-[#ba1609] hover:bg-[#8e1107] text-white px-4 py-2 rounded transition-colors"
            onClick={handleOK}
            disabled={!tempDate}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
});

CustomDateTimePicker.displayName = 'CustomDateTimePicker';

export default CustomDateTimePicker;
